import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { DownloadIcon } from "lucide-react";
import { useMemo } from "react";
import { campRegistrationsApi } from "../api/client";
import {
  useCampEventQuery,
  useCampEventsQuery,
  useCampRegistrationsQuery,
} from "../api/queries";
import { CampRegistrationsFilter } from "../ui/CampRegistrationsFilter";
import { CampRegistrationsTable } from "../ui/CampRegistrationsTable";

type CampRegistrationsSearch = {
  limit?: number;
  offset?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }> | string;
  eventId?: string;
  status?: string;
  classTypeId?: string;
  search?: string;
};

export function CampRegistrationsPage() {
  const nav = useNavigate({ from: "/app/camp-registrations" });
  const canExport = useActionPermission(["camp-registrations:export"]);
  const search = useSearch({
    from: "/app/camp-registrations",
  }) as CampRegistrationsSearch;

  const sortArray = useMemo((): Array<{
    field: string;
    dir: "asc" | "desc";
  }> => {
    const s = search.sort;
    if (Array.isArray(s) && s[0]) {
      const first = s[0] as { field?: string; dir?: string };
      return [
        {
          field: first.field ?? "submittedAt",
          dir: first.dir === "asc" ? "asc" : "desc",
        },
      ];
    }
    if (typeof s === "string" && s) {
      const field = s.startsWith("-") ? s.slice(1) : s;
      const dir: "asc" | "desc" = s.startsWith("-") ? "desc" : "asc";
      return [{ field, dir }];
    }
    return [{ field: "submittedAt", dir: "desc" }];
  }, [search.sort]);

  const { data: eventsData } = useCampEventsQuery();
  const eventId = String(search.eventId ?? eventsData?.items[0]?.id ?? "");
  const { data: eventDetail } = useCampEventQuery(eventId || undefined);

  const query = useMemo(
    () => ({
      limit: Number(search.limit ?? 20),
      offset: Number(search.offset ?? 0),
      sort: sortArray,
      eventId: eventId || undefined,
      status: search.status,
      classTypeId: search.classTypeId,
      search: search.search,
    }),
    [search, eventId, sortArray],
  );

  const { data, isLoading } = useCampRegistrationsQuery(query);
  const items = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  const classOptions =
    eventDetail?.classOptions
      ?.filter((item) => item.enabled)
      .map((item) => ({
        classTypeId: item.classTypeId,
        name: item.classTypeName ?? item.classTypeId,
      })) ?? [];

  const updateSearch = (updates: Record<string, unknown>) => {
    nav({ search: { ...search, ...updates } });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ໃບສະໝັກ Camp" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ໃບສະໝັກ Camp</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ຈັດການໃບສະໝັກຈາກ Summer Camp 2026 — ຕິດຕໍ່ ແລະ ແປງເປັນນັກຮຽນ
            </p>
            {eventDetail ? (
              <p className="mt-1 text-muted-foreground text-xs">
                Event: {eventDetail.title}
              </p>
            ) : null}
          </div>
          {canExport && eventId ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                window.open(
                  campRegistrationsApi.exportCsv(eventId),
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              <DownloadIcon className="mr-2 size-4" />
              ສົ່ງອອກ CSV
            </Button>
          ) : null}
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <CampRegistrationsFilter classOptions={classOptions} />
          <CampRegistrationsTable
            data={items}
            isLoading={isLoading}
            offset={Number(search.offset ?? 0)}
            limit={Number(search.limit ?? 20)}
            totalCount={total}
            sortBy={query.sort?.[0]?.field ?? "submittedAt"}
            sortOrder={(query.sort?.[0]?.dir ?? "desc") as "asc" | "desc"}
            onPaginationChange={(offset, limit) =>
              updateSearch({ offset, limit })
            }
            onSortingChange={(id, desc) =>
              updateSearch({
                sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                offset: 0,
              })
            }
            onView={(id) =>
              nav({ to: "/app/camp-registrations/$id", params: { id } })
            }
          />
        </div>
      </Main>
    </>
  );
}
